import React, { useCallback, useEffect, useRef, useState } from 'react';
import { API_CONFIG } from '../config';
import { ICONS } from '../constants';
import { useTranslation } from '../hooks/useTranslation';
import AiSpecResolutionModal, {
  MissingSpecBreakdown,
  SpecResolutionMap,
} from './AiSpecResolutionModal';

interface AiBidPanelProps {
  biddingSheetId: string;
  projectBudget?: number | null;
  currency?: string;
  isOpen: boolean;
  onClose: () => void;
  onMutated: () => void;
  /** Current Hours/Sum toggle on the sheet. */
  valueColumnMode?: 'hours' | 'sum';
  /** Visible specialization columns available for distribution. */
  allowedSpecializations?: string[];
}

interface ChatMessage {
  id?: string;
  role: 'user' | 'system';
  message: string;
  createdAt?: string;
  parsedBy?: 'rules' | 'llm' | 'heuristic';
}

interface ShotAnalysis {
  shotId: string;
  tags: {
    shotSize?: string;
    vfxSignals?: string[];
    complexityScore?: number;
    confidence?: number;
    rationale?: string;
  };
}

interface AiProgress {
  runId: string;
  status: string;
  phase: string;
  label: string | null;
  done: number;
  total: number;
  percent: number;
  errorMessage?: string | null;
  params?: { mode?: string };
}

const AiBidPanel: React.FC<AiBidPanelProps> = ({
  biddingSheetId,
  projectBudget,
  currency = 'USD',
  isOpen,
  onClose,
  onMutated,
  valueColumnMode = 'hours',
  allowedSpecializations = [],
}) => {
  const { t } = useTranslation();
  const [targetBudget, setTargetBudget] = useState<string>(
    projectBudget != null ? String(projectBudget) : ''
  );
  const [useProjectBudget, setUseProjectBudget] = useState(!!projectBudget);
  const [feeMode, setFeeMode] = useState<'artists_net' | 'gross_to_artists'>('artists_net');
  const [rateTier, setRateTier] = useState<'junior' | 'mid' | 'senior' | 'lead'>('mid');
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState('');
  const [runId, setRunId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [analyses, setAnalyses] = useState<ShotAnalysis[]>([]);
  const [progress, setProgress] = useState<AiProgress | null>(null);
  const [specModalOpen, setSpecModalOpen] = useState(false);
  const [specBreakdown, setSpecBreakdown] = useState<MissingSpecBreakdown[]>([]);
  const [existingSpecs, setExistingSpecs] = useState<string[]>([]);
  const pendingAllocateBodyRef = useRef<Record<string, unknown> | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const jobKindRef = useRef<'analyze' | 'allocate' | null>(null);

  const authHeaders = () => {
    const token = localStorage.getItem('spellbook_token');
    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  };

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const loadLatest = useCallback(async () => {
    try {
      const res = await fetch(`${API_CONFIG.API_URL}/bidding/${biddingSheetId}/ai/latest`, {
        headers: authHeaders(),
      });
      if (!res.ok) return;
      const data = await res.json();
      if (data.run?.id) setRunId(data.run.id);
      if (data.progress) setProgress(data.progress);
      setMessages(data.messages || []);
      setAnalyses(
        (data.analyses || []).map((a: any) => ({
          shotId: a.shotId,
          tags: a.tags || {},
        }))
      );
    } catch (e) {
      console.error(e);
    }
  }, [biddingSheetId]);

  const pollProgress = useCallback(
    async (jobRunId: string) => {
      try {
        const res = await fetch(
          `${API_CONFIG.API_URL}/bidding/${biddingSheetId}/ai/progress?runId=${encodeURIComponent(jobRunId)}`,
          { headers: authHeaders() }
        );
        if (!res.ok) return null;
        const data = await res.json();
        if (data.progress) setProgress(data.progress);
        return data as { progress: AiProgress | null; inProgress: boolean };
      } catch (e) {
        console.error(e);
        return null;
      }
    },
    [biddingSheetId]
  );

  const startPolling = useCallback(
    (jobRunId: string, kind: 'analyze' | 'allocate') => {
      stopPolling();
      jobKindRef.current = kind;
      setBusy(true);
      setRunId(jobRunId);

      const finish = async (p: AiProgress | null) => {
        stopPolling();
        setBusy(false);
        jobKindRef.current = null;
        await loadLatest();
        if (p?.status === 'completed') {
          if (kind === 'allocate') {
            setStatus(t('bidding.ai.allocated') || 'Allocation complete');
            onMutated();
          } else {
            setStatus(
              `${t('bidding.ai.analyzed') || 'Analyzed'}: ${p.done || p.total || ''}`
            );
            onMutated();
          }
        } else if (p?.status === 'failed') {
          setStatus(p.errorMessage || p.label || 'Failed');
        }
      };

      void pollProgress(jobRunId).then((first) => {
        if (first?.progress && !first.inProgress) {
          void finish(first.progress);
        }
      });

      pollRef.current = setInterval(() => {
        void pollProgress(jobRunId).then((data) => {
          if (!data) return;
          if (!data.inProgress) {
            void finish(data.progress);
          }
        });
      }, 800);
    },
    [loadLatest, onMutated, pollProgress, stopPolling, t]
  );

  useEffect(() => {
    if (isOpen) {
      void loadLatest().then(async () => {
        // Resume polling if a job is still running
        try {
          const res = await fetch(`${API_CONFIG.API_URL}/bidding/${biddingSheetId}/ai/progress`, {
            headers: authHeaders(),
          });
          if (!res.ok) return;
          const data = await res.json();
          if (data.inProgress && data.progress?.runId) {
            const mode = data.progress.params?.mode;
            startPolling(
              data.progress.runId,
              mode === 'analyze_only' ? 'analyze' : 'allocate'
            );
          }
        } catch {
          // ignore
        }
      });
    }
    return () => stopPolling();
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (projectBudget != null && useProjectBudget) {
      setTargetBudget(String(projectBudget));
    }
  }, [projectBudget, useProjectBudget]);

  if (!isOpen) return null;

  const handleAnalyze = async () => {
    setBusy(true);
    setStatus(t('bidding.ai.analyzing') || 'Analyzing shots…');
    setProgress(null);
    try {
      const res = await fetch(`${API_CONFIG.API_URL}/bidding/${biddingSheetId}/ai/analyze`, {
        method: 'POST',
        headers: authHeaders(),
      });
      const data = await res.json();
      if (res.status === 409 && data.progress?.runId) {
        startPolling(data.progress.runId, 'analyze');
        return;
      }
      if (!res.ok) throw new Error(data.error || 'Analyze failed');
      if (data.runId) {
        if (data.progress) setProgress(data.progress);
        startPolling(data.runId, 'analyze');
      } else {
        setBusy(false);
        setStatus(`${t('bidding.ai.analyzed') || 'Analyzed'}: ${data.analyzed ?? ''}`);
        await loadLatest();
      }
    } catch (e: any) {
      setStatus(e.message || 'Error');
      setBusy(false);
    }
  };

  const buildContextFields = () => ({
    valueColumnMode,
    ...(allowedSpecializations.length > 0
      ? { allowedSpecializations }
      : {}),
  });

  const buildAllocateBody = () => {
    const body: Record<string, unknown> = {
      useProjectBudget,
      feeMode,
      rateTier,
      ...buildContextFields(),
    };
    if (!useProjectBudget) {
      const n = Number(targetBudget);
      if (!(n > 0)) throw new Error(t('bidding.ai.budgetRequired') || 'Budget required');
      body.targetBudget = n;
    }
    return body;
  };

  const runAllocate = async (body: Record<string, unknown>) => {
    const res = await fetch(`${API_CONFIG.API_URL}/bidding/${biddingSheetId}/ai/allocate`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(body),
    });
    const data = await res.json();
    if (res.status === 409 && data.progress?.runId) {
      startPolling(data.progress.runId, 'allocate');
      return;
    }
    if (!res.ok) throw new Error(data.error || 'Allocate failed');
    if (data.runId) {
      if (data.progress) setProgress(data.progress);
      startPolling(data.runId, 'allocate');
    } else {
      setRunId(data.run?.id || null);
      setStatus(
        t('bidding.ai.allocated') ||
          `Done: ${data.plannedCount} assignments → ${Number(data.artistsBudget).toFixed(0)} ${currency}`
      );
      await loadLatest();
      onMutated();
      setBusy(false);
    }
  };

  const handleAllocate = async () => {
    setBusy(true);
    setStatus(t('bidding.ai.allocating') || 'Allocating…');
    setProgress(null);
    try {
      const body = buildAllocateBody();

      const previewRes = await fetch(
        `${API_CONFIG.API_URL}/bidding/${biddingSheetId}/ai/allocate/preview`,
        {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify(buildContextFields()),
        }
      );
      const preview = await previewRes.json();
      if (!previewRes.ok) throw new Error(preview.error || 'Preview failed');

      if (preview.needsAnalyze) {
        setBusy(false);
        setStatus(
          t('bidding.ai.analyzeFirst') ||
            'Run Analyze first — shots have not been analyzed yet.'
        );
        return;
      }

      const missing: string[] = preview.missingSpecializations || [];
      if (missing.length > 0) {
        setSpecBreakdown(preview.breakdown || []);
        setExistingSpecs(
          preview.existingSpecializations?.length
            ? preview.existingSpecializations
            : preview.effectiveSpecializations || []
        );
        pendingAllocateBodyRef.current = body;
        setSpecModalOpen(true);
        setBusy(false);
        setStatus('');
        return;
      }

      await runAllocate(body);
    } catch (e: any) {
      setStatus(e.message || 'Error');
      setBusy(false);
    }
  };

  const handleSpecResolutionConfirm = async (specResolution: SpecResolutionMap) => {
    setSpecModalOpen(false);
    const body = {
      ...(pendingAllocateBodyRef.current || buildAllocateBody()),
      specResolution,
    };
    pendingAllocateBodyRef.current = null;
    setBusy(true);
    setStatus(t('bidding.ai.allocating') || 'Allocating…');
    try {
      await runAllocate(body);
      onMutated();
    } catch (e: any) {
      setStatus(e.message || 'Error');
      setBusy(false);
    }
  };

  const handleUndo = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API_CONFIG.API_URL}/bidding/${biddingSheetId}/ai/undo`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ runId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Undo failed');
      setStatus(t('bidding.ai.undone') || 'Undone');
      await loadLatest();
      onMutated();
    } catch (e: any) {
      setStatus(e.message || 'Error');
    } finally {
      setBusy(false);
    }
  };

  const handleChat = async () => {
    const message = chatInput.trim();
    if (!message) return;
    setBusy(true);
    setChatInput('');
    setMessages((prev) => [...prev, { role: 'user', message }]);
    try {
      const res = await fetch(`${API_CONFIG.API_URL}/bidding/${biddingSheetId}/ai/chat`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ message, runId, ...buildContextFields() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Chat failed');
      if (data.runId) setRunId(data.runId);
      setMessages((prev) => [
        ...prev,
        {
          role: 'system',
          message: data.reply,
          parsedBy: data.parsedBy,
        },
      ]);
      if (data.mutated) onMutated();
    } catch (e: any) {
      setMessages((prev) => [...prev, { role: 'system', message: e.message || 'Error' }]);
    } finally {
      setBusy(false);
    }
  };

  const showBar = busy || (progress && progress.status !== 'completed' && progress.status !== 'failed');
  const barPercent = progress?.percent ?? (busy ? 2 : 0);
  const barLabel =
    progress?.label ||
    status ||
    (busy ? t('bidding.ai.working') || 'Working…' : '');

  return (
    <>
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={onClose}>
      <div
        className="w-full max-w-md h-full bg-gray-900 border-l border-gray-700 shadow-xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <h3 className="text-lg font-semibold text-white">
            {t('bidding.ai.title') || 'AI Bid Allocator'}
          </h3>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-white">
            <ICONS.X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-3 border-b border-gray-800">
          <p className="text-xs text-gray-500 truncate" title={allowedSpecializations.join(', ')}>
            {t('bidding.ai.contextMode') || 'Mode'}:{' '}
            {valueColumnMode === 'sum'
              ? t('bidding.sum') || 'Sum'
              : t('bidding.hours') || 'Hours'}
            {allowedSpecializations.length > 0 && (
              <>
                {' · '}
                {t('bidding.ai.contextColumns') || 'Columns'}:{' '}
                {allowedSpecializations.join(', ')}
              </>
            )}
          </p>
          <label className="flex items-center gap-2 text-sm text-gray-300">
            <input
              type="checkbox"
              checked={useProjectBudget}
              onChange={(e) => setUseProjectBudget(e.target.checked)}
              disabled={busy}
            />
            {t('bidding.ai.useProjectBudget') || 'Use project budget'}
            {projectBudget != null && (
              <span className="text-gray-500">
                ({projectBudget} {currency})
              </span>
            )}
          </label>
          {!useProjectBudget && (
            <input
              type="number"
              value={targetBudget}
              onChange={(e) => setTargetBudget(e.target.value)}
              placeholder={t('bidding.ai.targetBudget') || 'Target budget'}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded text-white text-sm"
              disabled={busy}
            />
          )}
          <div className="flex gap-2">
            <select
              value={feeMode}
              onChange={(e) => setFeeMode(e.target.value as any)}
              className="flex-1 px-2 py-2 bg-gray-800 border border-gray-600 rounded text-white text-sm"
              disabled={busy}
            >
              <option value="artists_net">{t('bidding.ai.feeArtistsNet') || 'Budget = artists'}</option>
              <option value="gross_to_artists">{t('bidding.ai.feeGross') || 'Gross → peel fees/VAT'}</option>
            </select>
            <select
              value={rateTier}
              onChange={(e) => setRateTier(e.target.value as any)}
              className="w-28 px-2 py-2 bg-gray-800 border border-gray-600 rounded text-white text-sm"
              disabled={busy}
            >
              <option value="junior">Junior</option>
              <option value="mid">Mid</option>
              <option value="senior">Senior</option>
              <option value="lead">Lead</option>
            </select>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              disabled={busy}
              onClick={handleAnalyze}
              className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 rounded text-sm text-white disabled:opacity-50"
            >
              {t('bidding.ai.analyze') || 'Analyze'}
            </button>
            <button
              disabled={busy}
              onClick={handleAllocate}
              className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-500 rounded text-sm text-white disabled:opacity-50"
            >
              {t('bidding.ai.allocate') || 'Allocate'}
            </button>
            <button
              disabled={busy}
              onClick={handleUndo}
              className="px-3 py-1.5 bg-amber-700 hover:bg-amber-600 rounded text-sm text-white disabled:opacity-50"
            >
              {t('bidding.ai.undo') || 'Undo'}
            </button>
          </div>

          {(showBar || status) && (
            <div className="space-y-1.5 pt-1">
              <div className="flex items-center justify-between text-xs text-gray-400 gap-2">
                <span className="truncate">{barLabel || status}</span>
                {progress && progress.total > 0 && (
                  <span className="shrink-0 tabular-nums">
                    {progress.done}/{progress.total} · {barPercent}%
                  </span>
                )}
                {!progress?.total && busy && <span className="shrink-0">{barPercent}%</span>}
              </div>
              {(showBar || (progress && progress.percent > 0)) && (
                <div className="h-2 bg-gray-800 rounded overflow-hidden border border-gray-700">
                  <div
                    className={`h-full transition-all duration-300 ${
                      progress?.status === 'failed'
                        ? 'bg-red-600'
                        : progress?.status === 'completed'
                          ? 'bg-teal-600'
                          : 'bg-indigo-500'
                    }`}
                    style={{ width: `${Math.min(100, Math.max(busy && barPercent < 2 ? 2 : 0, barPercent))}%` }}
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {analyses.length > 0 && (
          <div className="px-4 py-2 border-b border-gray-800 max-h-32 overflow-y-auto">
            <p className="text-xs text-gray-500 mb-1">
              {t('bidding.ai.analyses') || 'Analyses'}: {analyses.length}
            </p>
            <div className="flex flex-wrap gap-1">
              {analyses.slice(0, 24).map((a) => (
                <span
                  key={a.shotId}
                  className="text-[10px] px-1.5 py-0.5 rounded bg-gray-800 text-gray-300 border border-gray-700"
                  title={a.tags.rationale || ''}
                >
                  {a.tags.shotSize || '?'}·{a.tags.complexityScore ?? '-'}
                  {(a.tags.vfxSignals || []).length
                    ? `·${(a.tags.vfxSignals || []).length}sig`
                    : ''}
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {messages.length === 0 && (
            <p className="text-sm text-gray-500">
              {t('bidding.ai.chatHint') ||
                'Example: «урежь шот 5 в 2 раза», «урежь в 2 раза все подобные 5», «увеличь все roto на 20%»'}
            </p>
          )}
          {messages.map((m, i) => (
            <div
              key={m.id || i}
              className={`text-sm px-3 py-2 rounded-lg ${
                m.role === 'user'
                  ? 'bg-indigo-900/40 text-indigo-100 ml-6'
                  : 'bg-gray-800 text-gray-200 mr-6'
              }`}
            >
              {m.role === 'system' && m.parsedBy && (
                <span
                  className={`inline-block text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded mb-1 ${
                    m.parsedBy === 'llm'
                      ? 'bg-violet-900/60 text-violet-200'
                      : m.parsedBy === 'rules'
                        ? 'bg-teal-900/50 text-teal-200'
                        : 'bg-gray-700 text-gray-400'
                  }`}
                >
                  {m.parsedBy}
                </span>
              )}
              {m.message}
            </div>
          ))}
        </div>

        <div className="p-3 border-t border-gray-700 flex gap-2">
          <input
            value={chatInput}
            onChange={(e) => setChatInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleChat()}
            placeholder={t('bidding.ai.chatPlaceholder') || 'Correction command…'}
            className="flex-1 px-3 py-2 bg-gray-800 border border-gray-600 rounded text-white text-sm"
            disabled={busy}
          />
          <button
            onClick={handleChat}
            disabled={busy || !chatInput.trim()}
            className="px-3 py-2 bg-indigo-600 hover:bg-indigo-500 rounded text-white text-sm disabled:opacity-50"
          >
            {t('common.send') || 'Send'}
          </button>
        </div>
      </div>
    </div>

    <AiSpecResolutionModal
      isOpen={specModalOpen}
      onClose={() => {
        setSpecModalOpen(false);
        pendingAllocateBodyRef.current = null;
      }}
      breakdown={specBreakdown}
      existingSpecializations={existingSpecs}
      onConfirm={handleSpecResolutionConfirm}
    />
    </>
  );
};

export default AiBidPanel;
